import type { FC } from 'react';
import { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import ArrowDown from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import ArrowUp from '@wix/da-ds/pkg/Icons/16x16/ArrowUp';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import DropdownStateManager, {
  DropdownStateContext,
} from '@wix/da-ds/pkg/Dropdown/DropdownStateManager';
import ListItemButton from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemButton';

import s from './AppliesToDropdown.scss';

interface DropdownOption {
  label: string;
  value: string;
  subLabel?: string;
}

interface Props {
  id: string;
  className?: string;
  label: string;
  value: string;
  items: DropdownOption[];
  error?: string;
  isRequired?: boolean;
  onChange: (value: string) => void;
}

const AppliesToDropdownContent: FC<Props> = ({
  id,
  className,
  label,
  value,
  items,
  error,
  isRequired,
  onChange,
}) => {
  const { t } = useTranslation();
  const { isOpen, setToggleRef, setMenuRef, closeDropdown } =
    useContext(DropdownStateContext);

  const selectedItem = items.find(item => item.value === value);
  const Icon = isOpen ? ArrowUp : ArrowDown;

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    closeDropdown();
  };

  return (
    <div
      className={c(
        s['root'],
        isOpen && s['open'],
        error && s['error'],
        className
      )}
    >
      <FormElement
        inputId={id}
        label={
          <InputLabelText isRequired={isRequired} className="label">
            {label}
          </InputLabelText>
        }
        noBottomSpacing={false}
        details={
          error && (
            <InputErrorMessage id={`${id}-error`}>{error}</InputErrorMessage>
          )
        }
      >
        <div
          id={id}
          tabIndex={0}
          className={c(s['toggle'], 'ignore-react-onclickoutside')}
          ref={setToggleRef}
          role="combobox"
          aria-expanded={isOpen ? 'true' : 'false'}
          aria-haspopup="listbox"
        >
          {selectedItem ? (
            <div className={s['value']}>{selectedItem.label}</div>
          ) : (
            <div className={s['placeholder']}>{t('common.select')}</div>
          )}
          <Icon
            className={s['icon']}
            size={IconSize.SMALLER}
            disableDefaultColors
          />
        </div>
      </FormElement>
      {isOpen && (
        <div className={c(s['menu'], 'ds-surface-tertiary')} ref={setMenuRef}>
          {items.map(item => (
            <ListItemButton
              key={item.value}
              label={item.label}
              subLabel={item.subLabel}
              onContentClick={() => handleSelect(item.value)}
              className={s['menu-item']}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AppliesToDropdown: FC<Props> = props => (
  <DropdownStateManager closeOnLeave={false}>
    <AppliesToDropdownContent {...props} />
  </DropdownStateManager>
);

AppliesToDropdown.displayName = 'AppliesToDropdown';

export default AppliesToDropdown;
